import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { CATEGORY_LABELS } from '../../lib/utils';

const emptyTld = { tld: '', category: 'generic', description: '', is_active: true };

export default function AdminTlds() {
  const [tlds, setTlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTld);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadTlds(); }, []);

  const loadTlds = async () => {
    try { const data = await api.getTlds(); setTlds(data); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyTld); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ tld: t.tld, category: t.category, description: t.description || '', is_active: t.is_active }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.updateTld(editing.id, form);
        toast.success('TLD aktualisiert');
      } else {
        await api.createTld(form);
        toast.success('TLD erstellt');
      }
      setDialogOpen(false);
      loadTlds();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('TLD wirklich löschen?')) return;
    try { await api.deleteTld(id); toast.success('TLD gelöscht'); loadTlds(); } catch (e) { toast.error(e.message); }
  };

  const filtered = filter === 'all' ? tlds : tlds.filter(t => t.category === filter);

  return (
    <div data-testid="admin-tlds">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>TLDs</h1>
          <p className="text-sm text-muted-foreground mt-1">{tlds.length} TLDs insgesamt</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="admin-crud-create-button">
          <Plus className="w-4 h-4" /> Neue TLD
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        {['all', 'generic', 'country', 'new'].map(cat => (
          <Button key={cat} variant={filter === cat ? 'default' : 'outline'} size="sm" onClick={() => setFilter(cat)} className="text-xs">
            {cat === 'all' ? 'Alle' : CATEGORY_LABELS[cat]}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-crud-table">
          {filtered.map(t => (
            <Card key={t.id} className="rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <span className="font-bold text-lg text-slate-900">{t.tld}</span>
                    <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[t.category]}</Badge>
                  </div>
                  <Badge variant={t.is_active ? 'default' : 'secondary'} className="text-[10px]">{t.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                </div>
                {t.description && <p className="text-xs text-muted-foreground mb-2">{t.description}</p>}
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'TLD bearbeiten' : 'Neue TLD'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>TLD *</Label>
              <Input value={form.tld} onChange={e => setForm({...form, tld: e.target.value})} placeholder="z.B. .de oder de" data-testid="tld-input" />
              <p className="text-xs text-muted-foreground mt-1">Wird automatisch normalisiert (führender Punkt)</p>
            </div>
            <div>
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">Generisch</SelectItem>
                  <SelectItem value="country">Länder</SelectItem>
                  <SelectItem value="new">Neue TLDs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="z.B. Deutschland" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={!form.tld} data-testid="admin-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
